/**
 * Purchases facade used by the paywall. Picks the store adapter (lib/billing.ts: RevenueCat on
 * native, Stripe Checkout or mock on the web), applies the device policy (managed device / purchases
 * turned off) and records analytics. Ask to Buy leaves a pending purchase in the store state, shown by
 * the PendingPurchase overlay until the parent answers.
 */
import { deviceGate, productId, reconcilePending, type BillingPeriod, type PlanId } from '@sketchling/core';
import { api } from './api';
import { applyServerEntitlement, applyServerPending, currentStore, useMoney, type PurchaseResult } from './billing';
import { effectivePolicy } from './devicePolicy';
import { trackMoney } from './analytics';
import { useApp } from '../state/store';

export type { PurchaseResult };

export async function purchase(plan: PlanId, period: BillingPeriod): Promise<PurchaseResult> {
  const st = useApp.getState();
  const gate = deviceGate(effectivePolicy(), st.config);
  if (gate !== 'ok') {
    st.logEvent('Purchase Blocked', { plan, period, gate });
    trackMoney('purchase_fail', { sku: productId(plan, period), reason: gate });
    return { ok: false, reason: gate === 'blocked' ? 'blocked' : 'purchasesOff' };
  }
  const store = currentStore();
  const sku = productId(plan, period);
  const offering = useMoney.getState().invitedTrialDays ? 'invited_14d' : 'default';
  st.logEvent('Purchase Started', { plan, period, store: store.name });
  trackMoney('purchase_start', { sku, offering, store: store.name });
  const r = await store.purchase(plan, period);
  const ev = r.ok ? 'Purchase Completed' : r.reason === 'pending' ? 'Purchase Pending' : r.reason === 'cancelled' ? 'Purchase Cancelled' : 'Purchase Failed';
  useApp.getState().logEvent(ev, { plan, period, store: store.name, ...(r.ok ? { trial: r.trial } : { reason: r.reason }) });
  if (!r.ok && r.reason === 'pending') trackMoney('purchase_pending', { sku, store: store.name });
  else if (!r.ok && r.reason !== 'redirect') trackMoney('purchase_fail', { sku, reason: r.reason });
  if (r.ok) useApp.getState().setPendingPurchase(null);
  return r;
}

export async function restorePurchases(): Promise<boolean> {
  let found = false;
  try {
    found = await currentStore().restore();
  } catch {
    found = false;
  }
  trackMoney('restore', { found });
  return found;
}

/**
 * Re-check a pending (Ask to Buy) purchase: server entitlement first, then expiry. Returns the
 * resolution so the overlay can tell the child what happened.
 */
export async function checkPendingPurchase(): Promise<'approved' | 'expired' | 'declined' | 'waiting'> {
  const st = useApp.getState();
  const before = st.pendingPurchase;
  if (!before) return 'waiting';
  let serverPendingGone = false;
  if (st.account.accessToken) {
    try {
      const r = await api.restore();
      applyServerEntitlement(r.entitlement, before.plan);
      serverPendingGone = before.store === 'mock' && !r.pending;
      if (r.pending) applyServerPending(r.pending);
    } catch {
      /* offline: keep waiting */
    }
  }
  const now = useApp.getState();
  const { pending, resolution } = reconcilePending(now.pendingPurchase, now.entitlement.tier, new Date());
  if (resolution === 'approved' || resolution === 'expired') {
    now.setPendingPurchase(null);
    return resolution;
  }
  if (serverPendingGone) {
    now.setPendingPurchase(null);
    return 'declined';
  }
  if (!pending) now.setPendingPurchase(null);
  return 'waiting';
}
